import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faSearch } from "@fortawesome/sharp-regular-svg-icons";
import { useFeatureFlagEnabled } from "posthog-js/react";
import classNames from "classnames";
import { debounce } from "lodash";

import { Button } from "../buttons/Button/Button";
import { AppealState, AVAILABLE_APPEAL_STATES } from "../../types/appealState";
import { StatusDropdown } from "../StatusDropdown/StatusDropdown";
import { SearchModal } from "../SearchModal";
import { SearchChip } from "../SearchChip";
import { SearchCriteria } from "../../types/search";

import styles from "./search-bar.module.scss";

interface SearchBarProps {
  className?: string;
  onStatusFilterChange: (statuses: AppealState[]) => void;
  onResetFilters: () => void;
  onSearch: (criteria: SearchCriteria) => void;
  onSearchClear: () => void;
  searchCriteria: SearchCriteria;
  hasActiveSearch: boolean;
  isSearchModalOpen: boolean;
  onOpenSearchModal: () => void;
  onCloseSearchModal: () => void;
  selectedStatuses?: AppealState[];
}

export const SearchBar = ({
  className,
  onStatusFilterChange,
  onResetFilters,
  onSearch,
  onSearchClear,
  searchCriteria,
  hasActiveSearch,
  isSearchModalOpen,
  onOpenSearchModal,
  onCloseSearchModal,
  selectedStatuses: controlledSelectedStatuses,
}: SearchBarProps) => {
  const isSearchAndFilterV2Enabled = useFeatureFlagEnabled(
    "appeal-hub-search-and-filter-v2",
  );

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    controlledSelectedStatuses?.map(String) ?? [],
  );

  useEffect(() => {
    if (controlledSelectedStatuses !== undefined) {
      setSelectedStatuses(controlledSelectedStatuses.map(String));
    }
  }, [controlledSelectedStatuses]);

  const debouncedStatusFilterChange = useMemo(
    () =>
      debounce((statuses: AppealState[]) => {
        onStatusFilterChange(statuses);
      }, 1000),
    [onStatusFilterChange],
  );

  useEffect(() => {
    return () => {
      debouncedStatusFilterChange.cancel();
    };
  }, [debouncedStatusFilterChange]);

  const handleStatusChange = useCallback(
    (values: string[]) => {
      setSelectedStatuses(values);
      debouncedStatusFilterChange(values as AppealState[]);
    },
    [debouncedStatusFilterChange],
  );

  const handleResetFilters = useCallback(() => {
    setSelectedStatuses([]);
    debouncedStatusFilterChange.cancel();
    onResetFilters();
  }, [debouncedStatusFilterChange, onResetFilters]);

  const handleClear = () => {
    onSearchClear();
  };

  if (!isSearchAndFilterV2Enabled) {
    return null;
  }

  return (
    <div className={classNames(styles.searchBarContainer, className)}>
      <div className={styles.searchBar}>
        <div className={styles.filterSection}>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
          <StatusDropdown
            selectedStatuses={selectedStatuses as AppealState[]}
            onStatusChange={(statuses) =>
              handleStatusChange(statuses as string[])
            }
            availableStatuses={AVAILABLE_APPEAL_STATES}
          />
          <Button
            variant="text"
            size="2xs"
            onClick={handleResetFilters}
            className={styles.resetButton}
            disabled={selectedStatuses.length === 0}
          >
            Reset Filters
          </Button>
        </div>

        <div className={styles.searchSection}>
          <button className={styles.searchButton} onClick={onOpenSearchModal}>
            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
            <span className={styles.searchButtonText}>Search</span>
          </button>
        </div>
      </div>

      {hasActiveSearch && (
        <div className={styles.searchChipsContainer}>
          <SearchChip searchCriteria={searchCriteria} onRemove={handleClear} />
        </div>
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={onCloseSearchModal}
        onSearch={onSearch}
        initialCriteria={searchCriteria}
      />
    </div>
  );
};
